import { normalizeRing, uppercaseText } from './domain'
import { normalizeDoubtPath, sanitizeAnalysis } from './analysis-contract'
import type { FieldEvidence, SheetAnalysis, SheetDraft } from './types'
import { fieldCategory } from './field-validation'
import { readStoredFile } from './storage/fileStore'

const URL_KEY = 'palomar.analysis.url'
const TOKEN_KEY = 'palomar.analysis.token'
const CORRECTIONS_KEY = 'palomar.analysis.corrections'
const DEFAULT_ANALYSIS_URL = 'https://palomar-vision-api.palmitoonda.workers.dev/api/analyze-sheet'

export type AnalysisConfig = { url: string; token: string }

export class AnalysisConfigurationError extends Error {}

type CorrectionExample = { campo: string; detectado: string; corregido: string }

function correctionField(path: string) {
  return path.replace(/^pichones\.\d+\./, 'pichones.*.')
}

function correctionExamples(): CorrectionExample[] {
  try {
    const value = JSON.parse(localStorage.getItem(CORRECTIONS_KEY) || '[]')
    return Array.isArray(value) ? value.slice(-100) : []
  } catch {
    return []
  }
}

export function rememberAnalysisCorrection(campo: string, detectado: string, corregido: string) {
  const before = uppercaseText(detectado).trim()
  const after = uppercaseText(corregido).trim()
  if (!before || !after || before === after) return
  const key = correctionField(campo)
  const examples = correctionExamples().filter(item => !(item.campo === key && item.detectado === before))
  examples.push({ campo: key, detectado: before, corregido: after })
  localStorage.setItem(CORRECTIONS_KEY, JSON.stringify(examples.slice(-100)))
}

export function applyLearnedCorrection(campo: string, value: string | null): string | null {
  if (!value) return value
  const detected = uppercaseText(value).trim()
  const key = correctionField(campo)
  return [...correctionExamples()].reverse().find(item => item.campo === key && item.detectado === detected)?.corregido || detected
}

export function draftFieldValue(draft: SheetDraft, path: string): string {
  if (path === 'anio') return String(draft.anio)
  if (path === 'hoja') return String(draft.hoja)
  if (path.startsWith('padre.')) return String((draft.padre as unknown as Record<string, unknown>)[path.split('.')[1]] || '')
  if (path.startsWith('madre.')) return String((draft.madre as unknown as Record<string, unknown>)[path.split('.')[1]] || '')
  const match = path.match(/^pichones\.(\d+)\.(.+)$/)
  return match ? String((draft.pichones[Number(match[1])] as unknown as Record<string, unknown> | undefined)?.[match[2]] || '') : ''
}

export function getAnalysisConfig(): AnalysisConfig {
  return {
    url: localStorage.getItem(URL_KEY) || DEFAULT_ANALYSIS_URL,
    token: localStorage.getItem(TOKEN_KEY) || ''
  }
}

export function saveAnalysisConfig(config: AnalysisConfig) {
  localStorage.setItem(URL_KEY, config.url.trim())
  localStorage.setItem(TOKEN_KEY, config.token.trim())
}

export async function draftPhotoFile(draft: SheetDraft) {
  if (draft.photo) return draft.photo
  if (!draft.storedPhoto) return undefined
  const bytes = await readStoredFile(draft.storedPhoto.reference)
  if (!bytes) return undefined
  const contents = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
  return new File([contents], draft.storedPhoto.name, { type: draft.storedPhoto.mime })
}

export async function analyzeSheetPhoto(photo: File, ringPrefixes: Record<number, string> = {}): Promise<SheetAnalysis> {
  const config = getAnalysisConfig()
  if (!config.url || !config.token) {
    throw new AnalysisConfigurationError('Configura la URL del Worker y el código de análisis.')
  }
  const form = new FormData()
  form.append('image', photo, photo.name)
  form.append('ring_prefixes', JSON.stringify(ringPrefixes))
  const response = await fetch(config.url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${config.token}` },
    body: form
  })
  const payload = await response.json().catch(() => ({})) as Record<string, unknown>
  if (!response.ok) {
    const message = typeof payload.error === 'string' ? payload.error : 'No se pudo analizar la fotografía.'
    throw new Error(message)
  }
  return sanitizeAnalysis(payload)
}

export type ResolvedField = { campo: string; valor: string | null; confianza: number; motivo: string | null }

export async function analyzeFieldCrops(items: Array<{ campo: string; ocr: string; crop: Blob }>): Promise<ResolvedField[]> {
  const config = getAnalysisConfig()
  if (!config.url || !config.token) throw new AnalysisConfigurationError('Configura la URL del Worker y el código de análisis.')
  const endpoint = config.url.replace(/\/api\/analyze-sheet\/?$/, '/api/analyze-fields')
  const selected = items.slice(0, 24)
  const form = new FormData()
  form.append('manifest', JSON.stringify(selected.map(item => ({ campo: item.campo, tipo: fieldCategory(item.campo), valor_ocr: item.ocr }))))
  selected.forEach((item, index) => form.append(`crop_${index}`, item.crop, `campo-${index}.png`))
  const response = await fetch(endpoint, { method: 'POST', headers: { Authorization: `Bearer ${config.token}` }, body: form })
  const payload = await response.json().catch(() => ({})) as { error?: string; fields?: unknown[] }
  if (!response.ok) throw new Error(payload.error || 'No se pudieron resolver los recortes.')
  return Array.isArray(payload.fields) ? payload.fields.flatMap(value => {
    if (!value || typeof value !== 'object') return []
    const field = value as Record<string, unknown>
    if (typeof field.campo !== 'string') return []
    return [{ campo: field.campo, valor: typeof field.valor === 'string' ? uppercaseText(field.valor.trim()) : null, confianza: typeof field.confianza === 'number' ? Math.max(0, Math.min(100, Math.round(field.confianza))) : 0, motivo: typeof field.motivo === 'string' ? field.motivo : null }]
  }) : []
}

export function evidenceFromFullAnalysis(proposal: SheetAnalysis): Record<string, FieldEvidence> {
  const result: Record<string, FieldEvidence> = {}
  const doubtful = new Map(proposal.campos_dudosos.map(item => [normalizeDoubtPath(item.campo), item.motivo]))
  const add = (path: string, value: string | number | null) => {
    if (value === null || value === '') return
    const reason = doubtful.get(path)
    result[path] = { value: String(value), confidence: reason ? 45 : 85, origin: 'IA', valid: !reason, doubtful: Boolean(reason), reason }
  }
  add('anio', proposal.anio); add('hoja', proposal.hoja)
  for (const role of ['padre', 'madre'] as const) {
    add(`${role}.nombre`, proposal[role].nombre); add(`${role}.anilla`, proposal[role].anilla); add(`${role}.plumaje`, proposal[role].plumaje)
  }
  proposal.pichones.forEach((item, index) => {
    add(`pichones.${index}.anilla`, item.anilla); add(`pichones.${index}.plumaje`, item.plumaje)
    add(`pichones.${index}.sexo`, item.sexo); add(`pichones.${index}.observaciones`, item.observaciones)
  })
  return result
}

export function applyAnalysisProposal(draft: SheetDraft, proposal: SheetAnalysis): SheetDraft {
  const corrected: SheetAnalysis = {
    ...proposal,
    padre: {
      ...proposal.padre,
      nombre: applyLearnedCorrection('padre.nombre', proposal.padre.nombre),
      plumaje: applyLearnedCorrection('padre.plumaje', proposal.padre.plumaje)
    },
    madre: {
      ...proposal.madre,
      nombre: applyLearnedCorrection('madre.nombre', proposal.madre.nombre),
      plumaje: applyLearnedCorrection('madre.plumaje', proposal.madre.plumaje)
    },
    pichones: proposal.pichones.map((item, index) => ({
      ...item,
      plumaje: applyLearnedCorrection(`pichones.${index}.plumaje`, item.plumaje),
      sexo: applyLearnedCorrection(`pichones.${index}.sexo`, item.sexo),
      observaciones: applyLearnedCorrection(`pichones.${index}.observaciones`, item.observaciones)
    }))
  }
  return {
    ...draft,
    anio: corrected.anio ?? draft.anio,
    hoja: corrected.hoja ?? draft.hoja,
    padre: {
      ...draft.padre,
      nombre: uppercaseText(corrected.padre.nombre),
      anilla: normalizeRing(corrected.padre.anilla || ''),
      plumaje: uppercaseText(corrected.padre.plumaje)
    },
    madre: {
      ...draft.madre,
      nombre: uppercaseText(corrected.madre.nombre),
      anilla: normalizeRing(corrected.madre.anilla || ''),
      plumaje: uppercaseText(corrected.madre.plumaje)
    },
    pichones: corrected.pichones.map(item => ({
      id: crypto.randomUUID(),
      nombre: '',
      anilla: normalizeRing(item.anilla || ''),
      plumaje: uppercaseText(item.plumaje),
      sexo: uppercaseText(item.sexo),
      observaciones: uppercaseText(item.observaciones)
    })),
    doubtfulFields: corrected.campos_dudosos,
    fieldEvidence: evidenceFromFullAnalysis(corrected)
  }
}
export function doubtReason(draft: SheetDraft, path: string) {
  const normalized = normalizeDoubtPath(path)
  return draft.doubtfulFields?.find(item => normalizeDoubtPath(item.campo) === normalized)?.motivo
}

export function clearDoubt(draft: SheetDraft, path: string): SheetDraft {
  const normalized = normalizeDoubtPath(path)
  return {
    ...draft,
    doubtfulFields: draft.doubtfulFields?.filter(item => normalizeDoubtPath(item.campo) !== normalized)
  }
}

export function hasManualSheetData(draft: SheetDraft) {
  const birdHasData = (bird: SheetDraft['padre']) => Boolean(bird.nombre.trim() || bird.anilla.trim() || bird.plumaje.trim())
  return Boolean(draft.id || birdHasData(draft.padre) || birdHasData(draft.madre) || draft.pichones.length)
}
